import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import adminApi from '@/lib/adminApi';

const sectionMeta = {
  all: { label: 'All', badge: 'All' },
  artists: { label: 'Artists', badge: 'Artist' },
  collaborators: { label: 'Collaborators', badge: 'Collab' },
  native: { label: 'Native Review', badge: 'Native' },
  ai: { label: 'AI Safety', badge: 'AI' },
  support: { label: 'Support', badge: 'Help' },
  enquiries: { label: 'Enquiries', badge: 'Inbox' },
  projects: { label: 'Projects', badge: 'Job' },
  live_auditions: { label: 'Live Auditions', badge: 'Live' },
  artist_activity: { label: 'Artist Activity', badge: 'Update' },
  dashboard: { label: 'Dashboard', badge: 'Dash' },
};

const isNativeReviewPending = (artist) =>
  Boolean(String(artist.native_bio || '').trim()) && !artist.native_language_verified;

const joinParts = (parts) => parts.filter(Boolean).join(' | ');
const shortExcerpt = (value = '', maxLength = 180) => {
  const clean = String(value || '').replace(/\s+/g, ' ').trim();
  if (!clean) return '';
  return clean.length > maxLength ? `${clean.slice(0, maxLength - 1)}...` : clean;
};

const buildAlerts = ({ dashboard, artists, collaborators, aiStudio, support, enquiries, projects, liveAuditions, artistActivity }) => {
  const alerts = [];

  artists.forEach((artist) => {
    if (!artist.is_verified) {
      alerts.push({
        id: `artist-review-${artist.id}`,
        section: 'artists',
        type: 'Artist signup',
        priority: 'high',
        title: `${artist.name || 'Artist'} needs profile verification`,
        body: joinParts([artist.category, artist.subcategory, artist.city]) || 'New artist profile is waiting for admin review.',
        actionLabel: 'Review artist',
        to: `/admin/artists?focus=${artist.id}`,
        createdAt: artist.created_at || artist.updated_at || '',
      });
    }

    if (isNativeReviewPending(artist)) {
      alerts.push({
        id: `native-review-${artist.id}`,
        section: 'native',
        type: 'Native language',
        priority: 'medium',
        title: `${artist.name || 'Artist'} added a native-language bio`,
        body: joinParts([
          `Review ${artist.native_language || 'native language'} bio and approve badge text before it appears publicly.`,
          shortExcerpt(artist.native_bio),
        ]),
        actionLabel: 'Review native bio',
        to: `/admin/artists?focus=${artist.id}`,
        createdAt: artist.updated_at || artist.created_at || '',
      });
    }
  });

  collaborators.forEach((collaborator) => {
    if (!collaborator.is_verified) {
      alerts.push({
        id: `collaborator-review-${collaborator.id}`,
        section: 'collaborators',
        type: 'Collaborator signup',
        priority: 'high',
        title: `${collaborator.name || collaborator.organization_name || 'Collaborator'} needs verification`,
        body: joinParts([collaborator.organization_name, collaborator.organization_type, collaborator.city]) || 'New collaborator profile is waiting for admin review.',
        actionLabel: 'Review collaborator',
        to: `/admin/collaborators?focus=${collaborator.id}`,
        createdAt: collaborator.created_at || collaborator.updated_at || '',
      });
    }
  });

  (aiStudio.moderation_events || []).forEach((event) => {
    alerts.push({
      id: `ai-safety-${event.id}`,
      section: 'ai',
      type: 'AI safety',
      priority: 'critical',
      title: `Blocked AI Studio prompt from ${event.user_name || event.user_role || 'user'}`,
      body: `${(event.categories || []).join(', ') || 'Unsafe content'} | ${event.tool || 'AI Studio'}`,
      actionLabel: 'Open AI Studio',
      to: '/admin/ai-studio',
      createdAt: event.created_at || '',
    });
  });

  (support.messages || []).forEach((message) => {
    if ((message.status || 'new') === 'new') {
      alerts.push({
        id: `support-${message.id}`,
        section: 'support',
        type: 'Support message',
        priority: 'medium',
        title: message.subject || 'New support message',
        body: joinParts([message.name, message.email, message.phone]) || 'Support message needs review.',
        actionLabel: 'Open support',
        to: '/admin/support',
        createdAt: message.created_at || '',
      });
    }
  });

  (enquiries || []).forEach((enquiry) => {
    const status = String(enquiry.status || 'new').toLowerCase();
    if (status === 'new' || status === 'open') {
      alerts.push({
        id: `enquiry-${enquiry.id}`,
        section: 'enquiries',
        type: 'Enquiry',
        priority: 'medium',
        title: enquiry.subject || enquiry.name || 'New enquiry',
        body: joinParts([enquiry.email, enquiry.phone, enquiry.message]).slice(0, 160),
        actionLabel: 'Open enquiries',
        to: '/admin/enquiries',
        createdAt: enquiry.created_at || '',
      });
    }
  });

  (projects || []).forEach((project) => {
    if ((project.review_status || 'pending_review') === 'pending_review') {
      alerts.push({
        id: `project-review-${project.id}`,
        section: 'projects',
        type: 'Project approval',
        priority: 'high',
        title: `${project.title || 'Project'} needs admin approval`,
        body: joinParts([project.art_form, project.location, project.budget, project.poster_name]) || 'New project post is waiting for review.',
        actionLabel: 'Review project',
        to: `/admin/projects?focus=${project.id}`,
        createdAt: project.created_at || project.updated_at || '',
      });
    }
  });

  (liveAuditions || []).forEach((audition) => {
    if ((audition.review_status || 'pending_review') === 'pending_review') {
      alerts.push({
        id: `live-audition-review-${audition.id}`,
        section: 'live_auditions',
        type: 'Live audition approval',
        priority: 'high',
        title: `${audition.title || 'Live audition'} needs admin approval`,
        body: joinParts([audition.art_form, audition.city, audition.audition_date, audition.posted_by_name]) || 'New collaborator live audition is waiting for review.',
        actionLabel: 'Review audition',
        to: `/admin/live-auditions?focus=${audition.id}`,
        createdAt: audition.created_at || audition.updated_at || '',
      });
    }
  });

  (artistActivity || []).forEach((activity) => {
    const mediaFields = Array.isArray(activity.media_fields) ? activity.media_fields : [];
    const changedFields = Array.isArray(activity.changed_fields) ? activity.changed_fields : [];
    alerts.push({
      id: `artist-activity-${activity.id}`,
      section: 'artist_activity',
      type: activity.activity_type === 'video_added' ? 'Artist video' : 'Artist profile update',
      priority: mediaFields.length ? 'high' : 'medium',
      title: activity.title || `${activity.artist_name || 'Artist'} updated profile`,
      body: activity.body || joinParts([
        activity.artist_email,
        changedFields.length ? `Changed: ${changedFields.join(', ')}` : '',
        mediaFields.length ? `Media: ${mediaFields.join(', ')}` : '',
      ]),
      actionLabel: 'Review artist',
      to: activity.artist_id ? `/admin/artists?focus=${activity.artist_id}` : '/admin/artists',
      createdAt: activity.created_at || '',
    });
  });

  const readiness = dashboard?.launch_readiness || {};
  if ((readiness.pending_artist_reviews || 0) > artists.filter((artist) => !artist.is_verified).length) {
    alerts.push({
      id: 'dashboard-artist-review-count',
      section: 'dashboard',
      type: 'Dashboard',
      priority: 'medium',
      title: `${readiness.pending_artist_reviews} pending artist reviews`,
      body: 'Dashboard reports pending artist reviews. Open Artists to verify records.',
      actionLabel: 'Open artists',
      to: '/admin/artists',
      createdAt: '',
    });
  }

  return alerts.sort((a, b) => {
    const priorityRank = { critical: 0, high: 1, medium: 2, low: 3 };
    const byPriority = (priorityRank[a.priority] ?? 9) - (priorityRank[b.priority] ?? 9);
    if (byPriority !== 0) return byPriority;
    return String(b.createdAt || '').localeCompare(String(a.createdAt || ''));
  });
};

const priorityClass = (priority) => {
  if (priority === 'critical') return 'border-red-200 bg-red-50 text-red-700';
  if (priority === 'high') return 'border-orange-200 bg-orange-50 text-orange-700';
  return 'border-emerald-200 bg-emerald-50 text-emerald-700';
};

const sectionClass = (active) =>
  active
    ? 'border-[#FF6F00] bg-orange-50 text-[#FF6F00]'
    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50';

const AdminAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [dismissedIds, setDismissedIds] = useState([]);
  const [deletedIds, setDeletedIds] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeSection, setActiveSection] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSeen, setShowSeen] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);

  const loadAlerts = async () => {
    setLoading(true);
    setError('');
    try {
      const [
        dashboardResult,
        artistsResult,
        collaboratorsResult,
        aiStudioResult,
        supportResult,
        enquiriesResult,
        projectsResult,
        liveAuditionsResult,
        artistActivityResult,
        alertStateResult,
      ] = await Promise.allSettled([
        adminApi.get('/admin/dashboard'),
        adminApi.get('/admin/artists'),
        adminApi.get('/admin/collaborators'),
        adminApi.get('/admin/ai-studio'),
        adminApi.get('/admin/contact-messages'),
        adminApi.get('/admin/enquiries'),
        adminApi.get('/admin/projects'),
        adminApi.get('/admin/live-auditions'),
        adminApi.get('/admin/artist-activity'),
        adminApi.get('/admin/alerts/state'),
      ]);

      setAlerts(buildAlerts({
        dashboard: dashboardResult.value?.data || null,
        artists: artistsResult.value?.data?.artists || [],
        collaborators: collaboratorsResult.value?.data?.collaborators || [],
        aiStudio: aiStudioResult.value?.data || {},
        support: supportResult.value?.data || {},
        enquiries: enquiriesResult.value?.data?.enquiries || [],
        projects: projectsResult.value?.data?.projects || [],
        liveAuditions: liveAuditionsResult.value?.data?.auditions || [],
        artistActivity: artistActivityResult.value?.data?.activities || [],
      }));
      setDismissedIds(alertStateResult.value?.data?.seen_alert_ids || []);
      setDeletedIds(alertStateResult.value?.data?.deleted_alert_ids || []);
      setSelectedIds([]);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to load admin alerts.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAlerts();
  }, []);

  const sectionCounts = useMemo(() => {
    const activeAlerts = alerts.filter((alert) => !dismissedIds.includes(alert.id) && !deletedIds.includes(alert.id));
    return Object.keys(sectionMeta).reduce((counts, section) => ({
      ...counts,
      [section]: section === 'all' ? activeAlerts.length : activeAlerts.filter((alert) => alert.section === section).length,
    }), {});
  }, [alerts, dismissedIds, deletedIds]);

  const visibleAlerts = useMemo(() => {
    return alerts.filter((alert) => {
      if (activeSection !== 'all' && alert.section !== activeSection) return false;
      if (!showSeen && dismissedIds.includes(alert.id)) return false;
      if (!showDeleted && deletedIds.includes(alert.id)) return false;
      return true;
    });
  }, [alerts, activeSection, dismissedIds, deletedIds, showSeen, showDeleted]);

  const counts = useMemo(() => ({
    active: alerts.filter((alert) => !dismissedIds.includes(alert.id) && !deletedIds.includes(alert.id)).length,
    critical: alerts.filter((alert) => !dismissedIds.includes(alert.id) && !deletedIds.includes(alert.id) && alert.priority === 'critical').length,
    seen: alerts.filter((alert) => dismissedIds.includes(alert.id) && !deletedIds.includes(alert.id)).length,
    deleted: alerts.filter((alert) => deletedIds.includes(alert.id)).length,
  }), [alerts, dismissedIds, deletedIds]);

  const persistSeen = async (ids) => {
    if (!ids.length) return;
    const nextIds = Array.from(new Set([...dismissedIds, ...ids]));
    setDismissedIds(nextIds);
    setDeletedIds((current) => current.filter((id) => !ids.includes(id)));
    await adminApi.post('/admin/alerts/state', { alert_ids: ids, state: 'seen' });
  };

  const persistDeleted = async (ids) => {
    if (!ids.length) return;
    const nextIds = Array.from(new Set([...deletedIds, ...ids]));
    setDeletedIds(nextIds);
    setDismissedIds((current) => Array.from(new Set([...current, ...ids])));
    setSelectedIds((current) => current.filter((id) => !ids.includes(id)));
    await adminApi.post('/admin/alerts/state', { alert_ids: ids, state: 'deleted' });
  };

  const markSeen = (alertId) => persistSeen([alertId]);
  const deleteAlert = (alertId) => persistDeleted([alertId]);
  const markVisibleSeen = () => persistSeen(visibleAlerts.map((alert) => alert.id));
  const markSelectedSeen = () => persistSeen(selectedIds);
  const deleteSelected = () => persistDeleted(selectedIds);

  const toggleSelected = (alertId) => {
    setSelectedIds((current) =>
      current.includes(alertId) ? current.filter((id) => id !== alertId) : [...current, alertId]
    );
  };

  const allVisibleSelected = visibleAlerts.length > 0 && visibleAlerts.every((alert) => selectedIds.includes(alert.id));
  const toggleSelectVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((current) => current.filter((id) => !visibleAlerts.some((alert) => alert.id === id)));
    } else {
      setSelectedIds((current) => Array.from(new Set([...current, ...visibleAlerts.map((alert) => alert.id)])));
    }
  };

  const clearDeleted = async () => {
    if (!deletedIds.length) return;
    const idsToRestore = [...deletedIds];
    setDeletedIds([]);
    setDismissedIds((current) => current.filter((id) => !idsToRestore.includes(id)));
    await adminApi.post('/admin/alerts/state', { alert_ids: idsToRestore, state: 'active' });
  };

  return (
    <div className="space-y-6">
      <section className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-lg sm:p-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#FF6F00]">Admin alerts</p>
            <h2 className="mt-2 text-3xl font-bold text-gray-900">Things needing attention</h2>
            <p className="mt-3 max-w-3xl text-gray-600">
              Alerts are grouped by section. Select one or many alerts, mark them seen, or hide/delete alerts after handling them.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={loadAlerts} className="btn-outline">Refresh</button>
            <button type="button" onClick={markVisibleSeen} disabled={!visibleAlerts.length} className="btn-primary disabled:bg-gray-300">
              Mark all visible seen
            </button>
          </div>
        </div>
      </section>

      <section className="grid gap-4 sm:grid-cols-4">
        <div className="rounded-[1.75rem] border border-orange-100 bg-white p-5 shadow-md">
          <p className="text-sm text-gray-500">Active alerts</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{counts.active}</p>
        </div>
        <div className="rounded-[1.75rem] border border-red-100 bg-white p-5 shadow-md">
          <p className="text-sm text-gray-500">Critical safety</p>
          <p className="mt-2 text-3xl font-bold text-red-700">{counts.critical}</p>
        </div>
        <div className="rounded-[1.75rem] border border-emerald-100 bg-white p-5 shadow-md">
          <p className="text-sm text-gray-500">Seen</p>
          <p className="mt-2 text-3xl font-bold text-emerald-700">{counts.seen}</p>
        </div>
        <div className="rounded-[1.75rem] border border-slate-100 bg-white p-5 shadow-md">
          <p className="text-sm text-gray-500">Hidden/deleted</p>
          <p className="mt-2 text-3xl font-bold text-slate-700">{counts.deleted}</p>
        </div>
      </section>

      <section className="rounded-[2rem] border border-orange-100 bg-white p-5 shadow-lg sm:p-6">
        <div className="flex flex-wrap gap-2">
          {Object.entries(sectionMeta).map(([section, meta]) => (
            <button
              key={section}
              type="button"
              onClick={() => {
                setActiveSection(section);
                setSelectedIds([]);
              }}
              className={`rounded-full border px-4 py-2 text-sm font-bold transition ${sectionClass(activeSection === section)}`}
            >
              <span className="mr-2">{meta.badge}</span>
              {meta.label}
              <span className="ml-2 rounded-full bg-white/80 px-2 py-0.5 text-xs">{sectionCounts[section] || 0}</span>
            </button>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-4">
          <label className="inline-flex items-center gap-3 text-sm font-bold text-gray-700">
            <input type="checkbox" checked={showSeen} onChange={(event) => setShowSeen(event.target.checked)} />
            Show seen
          </label>
          <label className="inline-flex items-center gap-3 text-sm font-bold text-gray-700">
            <input type="checkbox" checked={showDeleted} onChange={(event) => setShowDeleted(event.target.checked)} />
            Show deleted
          </label>
          <button type="button" onClick={toggleSelectVisible} disabled={!visibleAlerts.length} className="btn-outline disabled:opacity-50">
            {allVisibleSelected ? 'Clear all visible' : 'Select all visible'}
          </button>
          <button type="button" onClick={markSelectedSeen} disabled={!selectedIds.length} className="btn-primary disabled:bg-gray-300">
            Mark selected seen
          </button>
          <button type="button" onClick={deleteSelected} disabled={!selectedIds.length} className="rounded-full border border-red-200 px-4 py-2 text-sm font-bold text-red-600 hover:bg-red-50 disabled:opacity-50">
            Delete selected
          </button>
          {counts.deleted > 0 && (
            <button type="button" onClick={clearDeleted} className="rounded-full border border-slate-200 px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50">
              Restore deleted alerts
            </button>
          )}
          <span className="text-sm font-semibold text-gray-500">{selectedIds.length} selected</span>
        </div>
      </section>

      {error && <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {loading ? (
        <div className="text-gray-500">Loading alerts...</div>
      ) : visibleAlerts.length === 0 ? (
        <section className="rounded-[2rem] border border-emerald-100 bg-white p-8 text-center shadow-lg">
          <h3 className="text-2xl font-bold text-gray-900">No alerts in this view</h3>
          <p className="mt-2 text-gray-600">Try another section, show seen alerts, or refresh after new activity.</p>
        </section>
      ) : (
        <section className="overflow-hidden rounded-[2rem] border border-orange-100 bg-white shadow-lg">
          <div className="divide-y divide-orange-100">
            {visibleAlerts.map((alert) => {
              const seen = dismissedIds.includes(alert.id);
              const deleted = deletedIds.includes(alert.id);
              const selected = selectedIds.includes(alert.id);
              const meta = sectionMeta[alert.section] || sectionMeta.dashboard;
              return (
                <div key={alert.id} className={`p-5 sm:p-6 ${seen ? 'bg-gray-50/70 opacity-75' : ''} ${deleted ? 'bg-slate-50 opacity-60' : ''}`}>
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="max-w-4xl">
                      <div className="flex flex-wrap items-center gap-2">
                        <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-bold text-slate-700">
                          <input type="checkbox" checked={selected} onChange={() => toggleSelected(alert.id)} />
                          Select
                        </label>
                        <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-bold text-white">
                          {meta.badge} {meta.label}
                        </span>
                        <span className={`rounded-full border px-3 py-1 text-xs font-bold ${priorityClass(alert.priority)}`}>
                          {alert.priority}
                        </span>
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">{alert.type}</span>
                        {seen && <span className="rounded-full bg-gray-200 px-3 py-1 text-xs font-bold text-gray-700">Seen</span>}
                        {deleted && <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-bold text-red-700">Deleted</span>}
                      </div>
                      <h3 className="mt-3 text-xl font-bold text-gray-900">{alert.title}</h3>
                      <p className="mt-2 text-sm leading-6 text-gray-600">{alert.body}</p>
                      {alert.createdAt && <p className="mt-2 text-xs text-gray-400">{alert.createdAt}</p>}
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <Link to={alert.to} className="btn-outline">{alert.actionLabel}</Link>
                      {!seen && (
                        <button type="button" onClick={() => markSeen(alert.id)} className="rounded-full border border-emerald-200 px-4 py-2 text-sm font-bold text-emerald-700 hover:bg-emerald-50">
                          Mark seen
                        </button>
                      )}
                      {!deleted && (
                        <button type="button" onClick={() => deleteAlert(alert.id)} className="rounded-full border border-red-200 px-4 py-2 text-sm font-bold text-red-600 hover:bg-red-50">
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

export default AdminAlerts;
